/**
 * QuickFeedbackPage — public, no login required.
 *
 * Built 2026-06-05 to capture feedback from anyone who tried the platform
 * — including the founder's wedding-event quick demo where the user did
 * not register. POSTs to /api/validator-feedback (which accepts all
 * optional fields). Feedback shows up in /admin/feedback.
 *
 * NO LLM. Single self-contained component. No external dependencies
 * beyond axios + lucide-react (already in the project).
 */
import { useState } from 'react';
import axios from 'axios';
import { CheckCircle2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function QuickFeedbackPage() {
  const [encounterId, setEncounterId] = useState('');
  const [freeText, setFreeText] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [verdict, setVerdict] = useState('other');
  const [usefulness, setUsefulness] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!freeText || freeText.trim().length < 5) {
      setError('Please write at least one sentence of feedback.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {
        source_route: '/feedback',
        encounter_id: encounterId.trim() || null,
        verdict,
        usefulness: usefulness || null,
        free_text: freeText.trim(),
        validator_name: name.trim() || null,
        validator_email: email.trim() || null,
      };
      const r = await axios.post(`${API}/validator-feedback`, payload);
      if (r.data && r.data.ok) {
        setSuccess(r.data.feedback_id);
        setFreeText('');
        setEncounterId('');
      } else {
        setError('Submission accepted but no confirmation returned. Your feedback may still have been saved.');
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Unable to submit. Please try again or email sadasivam.eniasivam@paiglobalhealth.com directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="quick-feedback-page">
      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="border-t-4 border-emerald-600 pt-6 mb-6">
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-800 mb-2">P.Ai Global Health · Quick feedback</p>
          <h1 className="text-2xl font-bold text-slate-900 leading-tight mb-3">
            Tried it? Tell me what worked and what didn't.
          </h1>
          <p className="text-sm text-slate-700 leading-relaxed">
            No login required. If you tested the platform at an event, a clinic, or just curiously,
            your feedback shapes the next engine commit. Be brutal — I'd rather know a flag missed
            than hear "looks great."
          </p>
        </div>

        {success ? (
          <div data-testid="feedback-success" className="border-2 border-emerald-500 bg-emerald-50 rounded-lg p-5 mb-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-emerald-900 mb-1">Thank you — feedback recorded.</p>
                <p className="text-sm text-emerald-800">Reference ID: <code className="bg-white px-2 py-0.5 rounded text-xs">{success}</code></p>
                <p className="text-sm text-emerald-800 mt-2">If you have more feedback, you can submit again below.</p>
                <button
                  data-testid="feedback-submit-another"
                  onClick={() => setSuccess(null)}
                  className="mt-3 text-sm font-semibold text-emerald-700 underline cursor-pointer"
                >
                  Submit another →
                </button>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5 bg-white border border-slate-300 rounded-lg p-6 shadow-sm" data-testid="feedback-form">
            <div>
              <label className="block text-sm font-bold text-slate-800 mb-1.5">
                Encounter ID (optional)
                <span className="ml-2 font-normal text-xs text-slate-500">
                  e.g., PGH-024279 — shown at the top of any SOAP output. Leave blank if you don't remember.
                </span>
              </label>
              <input
                data-testid="feedback-encounter-id"
                type="text"
                value={encounterId}
                onChange={(e) => setEncounterId(e.target.value)}
                placeholder="PGH-XXXXXX"
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm font-mono"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-800 mb-1.5">
                Your feedback <span className="text-red-600">*</span>
                <span className="ml-2 font-normal text-xs text-slate-500">
                  What did the platform get right, miss, or get wrong? Specific examples are gold.
                </span>
              </label>
              <textarea
                data-testid="feedback-free-text"
                required
                rows={6}
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                placeholder="e.g., I entered weight loss + fever + night sweats; it returned 'fever NOS' — should have flagged B-symptom triad / lymphoma..."
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm leading-relaxed"
              />
              <p className="text-xs text-slate-500 mt-1">{freeText.length} characters</p>
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-800 mb-1.5">
                Overall verdict
              </label>
              <select
                data-testid="feedback-verdict"
                value={verdict}
                onChange={(e) => setVerdict(e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white"
              >
                <option value="other">Other / mixed</option>
                <option value="looks_correct">Looks clinically correct</option>
                <option value="flag_missed">A dangerous flag was MISSED</option>
                <option value="flag_extra">An extra / over-flagged finding</option>
                <option value="severity_wrong">Severity was wrong</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-800 mb-1.5">
                Would you use this in your setting? (optional)
              </label>
              <select
                data-testid="feedback-usefulness"
                value={usefulness}
                onChange={(e) => setUsefulness(e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white"
              >
                <option value="">— No answer —</option>
                <option value="would_use">Would use today (with standard clinician review)</option>
                <option value="maybe_with_changes">Maybe, with changes</option>
                <option value="unlikely">Unlikely in current form</option>
                <option value="unsure">Unsure / need more time</option>
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-slate-800 mb-1.5">
                  Your name (optional)
                </label>
                <input
                  data-testid="feedback-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Dr. Jane Smith, MD"
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-800 mb-1.5">
                  Email (optional)
                </label>
                <input
                  data-testid="feedback-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                />
              </div>
            </div>

            <p className="text-xs text-slate-500 leading-relaxed bg-slate-50 border border-slate-200 rounded px-3 py-2">
              <strong>Not HIPAA-compliant.</strong> This is a synthetic-data sandbox. The production architecture is designed to be HIPAA-Eligible.
              Do not include real patient identifiers. Your feedback is stored privately and reviewed
              only by the founder. No public attribution without your explicit later consent.
            </p>

            {error && (
              <div data-testid="feedback-error" className="border border-red-300 bg-red-50 rounded p-3 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <button
              data-testid="feedback-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-400 disabled:cursor-not-allowed text-white font-bold py-3 rounded text-sm transition-colors cursor-pointer"
            >
              {submitting ? 'Submitting…' : 'Submit feedback'}
            </button>
          </form>
        )}

        <div className="mt-6 text-xs text-slate-500 leading-relaxed">
          <p>
            Or email directly: <a href="mailto:sadasivam.eniasivam@paiglobalhealth.com" className="text-emerald-700 font-semibold underline">sadasivam.eniasivam@paiglobalhealth.com</a>
          </p>
          <p className="mt-1">
            Live build status: <a href={`${BACKEND_URL}/api/system/build-status`} target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline">/api/system/build-status</a>
            {' · '}
            <a href="/" className="text-emerald-700 underline">Home</a>
          </p>
        </div>
      </div>
    </div>
  );
}
